import type {
	FontModel,
	OfferingFrameModel,
} from '@sosocio/creator-types/app';
import type {
	AddressModel,
	BorderImageModel,
	OfferingModel,
} from '@sosocio/creator-types/database';
import type {
	PageModel,
	PageObjectModel,
} from '@sosocio/creator-types/project';
import { resolveOfferingPageAssetSource } from '@sosocio/tools/offering-page-assets';
import type {
	EditorDrawAssetSource,
	EditorDrawLoadPhotoOptions,
	EditorDrawMaximumObjectSize,
	EditorDrawOfferingModel,
	EditorDrawStoreDependencies,
} from '@sosocio/ui/editor-draw';
import { editorDrawStoreDependenciesKey } from '@sosocio/ui/editor-draw';
import {
	AppDataModule,
	FontModule,
	PhotosModule,
	ProductStateModule,
	ThemeDataModule,
} from 'store';
import { getBackgroundPatternScaleRatio } from 'tools/background-pattern';
import formatAddress from 'tools/format-address';
import getBucketUrl from 'tools/get-bucket-url';
import maxObjectSize from 'tools/max-object-size';
import type { App as VueApp } from 'vue';

/**
 * Formats an address with Creator's country and state catalog.
 *
 * @param addressModel Project address rendered by the canvas.
 * @returns Ordered address lines ready for text layout.
 */
function formatProjectAddress(addressModel: AddressModel): string[] {
	return formatAddress(addressModel);
}

/**
 * Resolves the catalog multiplier for a background pattern.
 *
 * @param source Background pattern reference stored on the page.
 * @returns Catalog multiplier or the established fallback value.
 */
function getCreatorBackgroundPatternScaleRatio(source: PageModel['bgpattern']): number {
	return getBackgroundPatternScaleRatio(
		source,
		ThemeDataModule.getPageBackgrounds,
	);
}

/**
 * Finds a border image in Creator's theme catalog.
 *
 * @param id Border image identifier.
 * @returns Matching border image when it is available.
 */
function getCreatorBorderImage(id: BorderImageModel['id']): BorderImageModel | undefined {
	return ThemeDataModule.getBorderImage(id);
}

/**
 * Finds a font in Creator's loaded font collection.
 *
 * @param id Font identifier used by a page object.
 * @returns Matching font model when it is available.
 */
function getCreatorFont(id: FontModel['id']): FontModel | undefined {
	return FontModule.getById(id);
}

/**
 * Calculates the maximum size allowed for a page object in Creator.
 *
 * @param objectModel Page object whose source and crop dimensions are evaluated.
 * @param offeringModel Offering whose DPI and processing rules should be applied.
 * @returns Maximum render width and height.
 */
function getCreatorMaximumObjectSize(
	objectModel: PageObjectModel,
	offeringModel?: EditorDrawOfferingModel,
	ignoreMaximumHeight = false,
): EditorDrawMaximumObjectSize {
	const photoModel = (
		objectModel.photoid
			? PhotosModule.getById(objectModel.photoid)
			: undefined
	);
	const photoData = (
		photoModel
			? {
				height: photoModel.full_height,
				width: photoModel.full_width,
			}
			: undefined
	);

	return maxObjectSize(
		{
			cropheight: objectModel.cropheight,
			cropwidth: objectModel.cropwidth,
			maxheight: ignoreMaximumHeight
				? undefined
				: objectModel.maxheight,
			maxwidth: objectModel.maxwidth,
			type: objectModel.type,
		},
		photoData,
		offeringModel as OfferingModel | undefined,
	);
}

/**
 * Starts loading an offering frame and reads the current cached image.
 *
 * @param offeringFrameModel Offering frame requested by the renderer.
 * @returns Cached frame image when the asynchronous load has completed.
 */
function getCreatorOfferingFrameImage(
	offeringFrameModel: OfferingFrameModel,
): HTMLImageElement | undefined {
	const creatorFrameModel = offeringFrameModel;

	AppDataModule.getAndSetOfferingFrameImage(creatorFrameModel.imageModel);

	return AppDataModule.findOfferingFrameImage(creatorFrameModel.imageModel);
}

/**
 * Starts loading an offering mask and reads the current cached image.
 *
 * @param offeringModel Offering whose mask should be loaded.
 * @param pageIndex Zero-based offering page index.
 * @returns Cached mask image when the asynchronous load has completed.
 */
function getCreatorOfferingMaskImage(
	offeringModel: EditorDrawOfferingModel,
	pageIndex: number,
): HTMLImageElement | undefined {
	AppDataModule.getAndSetOfferingMaskImage({
		offering: offeringModel as OfferingModel,
		pageIndex,
	});

	return AppDataModule.findOfferingMaskImage(
		offeringModel as OfferingModel,
		pageIndex,
	);
}

/**
 * Starts loading an offering overlay and reads the current cached image.
 *
 * @param offeringModel Offering whose overlay should be loaded.
 * @param pageIndex Zero-based offering page index.
 * @returns Cached overlay image when the asynchronous load has completed.
 */
function getCreatorOfferingOverlayImage(
	offeringModel: EditorDrawOfferingModel,
	pageIndex: number,
): HTMLImageElement | undefined {
	AppDataModule.getAndSetOfferingOverlayImage({
		offering: offeringModel as OfferingModel,
		pageIndex,
	});

	return AppDataModule.findOfferingOverlayImage(
		offeringModel as OfferingModel,
		pageIndex,
	);
}

/**
 * Resolves a page's position in Creator's active project.
 *
 * @param pageModel Page rendered by the canvas.
 * @returns Zero-based index used by offering page assets.
 */
function getCreatorPageIndex(pageModel: PageModel): number {
	return ProductStateModule.getPageIndex(pageModel);
}

/**
 * Loads a font face through Creator's font store.
 *
 * @param id Font identifier requested by the renderer.
 * @returns Loaded font model.
 */
function loadCreatorFont(id: FontModel['id']): Promise<FontModel> {
	return FontModule.loadModel(id) as Promise<FontModel>;
}

/**
 * Loads an embeddable data URI through Creator's font store.
 *
 * @param id Font identifier requested by the renderer.
 * @returns Font model containing the loaded data URI.
 */
function loadCreatorFontDataUri(id: FontModel['id']): Promise<FontModel> {
	return FontModule.loadDataUri(id) as Promise<FontModel>;
}

/**
 * Loads a page-object photo without mutating Creator's product store.
 *
 * @param options Renderer-owned photo loading options.
 * @returns Updated detached page object.
 */
function loadCreatorPageObjectPhoto(
	options: EditorDrawLoadPhotoOptions,
): Promise<PageObjectModel> {
	return ProductStateModule.loadPageObjectPhoto({
		...options,
		originalPageObject: options.originalPageObject,
		noStore: true,
		offeringModel: options.offeringModel as OfferingModel | undefined,
	}) as Promise<PageObjectModel>;
}

/**
 * Releases an offering frame from Creator's image cache.
 *
 * @param offeringFrameModel Offering frame no longer used by the renderer.
 */
function releaseCreatorOfferingFrameImage(offeringFrameModel: OfferingFrameModel): void {
	AppDataModule.unsetOfferingFrameImage(offeringFrameModel);
}

/**
 * Releases an offering mask from Creator's image cache.
 *
 * @param offeringModel Offering whose mask is no longer used.
 * @param pageIndex Zero-based offering page index.
 */
function releaseCreatorOfferingMaskImage(
	offeringModel: EditorDrawOfferingModel,
	pageIndex: number,
): void {
	AppDataModule.unsetOfferingMaskImage({
		offering: offeringModel as OfferingModel,
		pageIndex,
	});
}

/**
 * Releases an offering overlay from Creator's image cache.
 *
 * @param offeringModel Offering whose overlay is no longer used.
 * @param pageIndex Zero-based offering page index.
 */
function releaseCreatorOfferingOverlayImage(
	offeringModel: EditorDrawOfferingModel,
	pageIndex: number,
): void {
	AppDataModule.unsetOfferingOverlayImage({
		offering: offeringModel as OfferingModel,
		pageIndex,
	});
}

/**
 * Resolves a page asset reference against Creator's app-files bucket.
 *
 * @param source Absolute URL or app-files-relative source.
 * @returns URL that can be passed to the image loading pipeline.
 */
function resolveCreatorAppFileSource(source: string): string {
	if (source.substring(
		0,
		4,
	) === 'http') {
		return source;
	}

	return `${getBucketUrl('appfiles')}${source}`;
}

/**
 * Resolves a mask source from Creator's page-aware offering catalog.
 *
 * @param offeringModel Offering whose mask is required.
 * @param pageIndex Zero-based offering page index.
 * @returns Stable cache identity and URL, or `null` when no mask is configured.
 */
function resolveCreatorOfferingMaskAssetSource(
	offeringModel: EditorDrawOfferingModel,
	pageIndex: number,
): EditorDrawAssetSource | null {
	return resolveOfferingPageAssetSource({
		assetKind: 'mask',
		fallbackUrl: offeringModel.mask,
		models: AppDataModule.offeringmaskimages,
		offeringId: offeringModel.id,
		pageIndex,
	});
}

/**
 * Resolves an overlay source from Creator's page-aware offering catalog.
 *
 * @param offeringModel Offering whose overlay is required.
 * @param pageIndex Zero-based offering page index.
 * @returns Stable cache identity and URL, or `null` when no overlay is configured.
 */
function resolveCreatorOfferingOverlayAssetSource(
	offeringModel: EditorDrawOfferingModel,
	pageIndex: number,
): EditorDrawAssetSource | null {
	return resolveOfferingPageAssetSource({
		assetKind: 'overlay',
		fallbackUrl: offeringModel.overlay,
		models: AppDataModule.offeringoverlayimages,
		offeringId: offeringModel.id,
		pageIndex,
	});
}

export const creatorEditorDrawStoreDependencies: EditorDrawStoreDependencies = {
	formatAddress: formatProjectAddress,
	getBackgroundPatternScaleRatio: getCreatorBackgroundPatternScaleRatio,
	getBorderImage: getCreatorBorderImage,
	getFont: getCreatorFont,
	getMaximumObjectSize: getCreatorMaximumObjectSize,
	getOfferingFrameImage: getCreatorOfferingFrameImage,
	getOfferingMaskImage: getCreatorOfferingMaskImage,
	getOfferingOverlayImage: getCreatorOfferingOverlayImage,
	getPageIndex: getCreatorPageIndex,
	loadFont: loadCreatorFont,
	loadFontDataUri: loadCreatorFontDataUri,
	loadPageObjectPhoto: loadCreatorPageObjectPhoto,
	releaseOfferingFrameImage: releaseCreatorOfferingFrameImage,
	releaseOfferingMaskImage: releaseCreatorOfferingMaskImage,
	releaseOfferingOverlayImage: releaseCreatorOfferingOverlayImage,
	resolveAppFileSource: resolveCreatorAppFileSource,
	resolveOfferingMaskAssetSource: resolveCreatorOfferingMaskAssetSource,
	resolveOfferingOverlayAssetSource: resolveCreatorOfferingOverlayAssetSource,
};

/**
 * Provides Creator's store-backed render data to every shared EditorDraw instance.
 *
 * @param app Creator Vue application.
 */
export function installCreatorEditorDrawStoreDependencies(app: VueApp): void {
	app.provide(
		editorDrawStoreDependenciesKey,
		creatorEditorDrawStoreDependencies,
	);
}
